import React from 'react';
import { Feedback, FileUploadFieldProps } from '@placer-ui/components';
import { Controller, useFormContext } from 'react-hook-form';
import type { ControllerProps, FieldValues } from 'react-hook-form/dist/types';
import { SupportRequestsFileUpload } from 'features/my-zone/support-requests/components/support-requests-modals/components/support-requests-file-upload/support-requests-file-upload';

export type ControlledMultiSelectProps<T extends FieldValues = FieldValues> = Omit<
    ControllerProps<T>,
    'render'
> &
    Omit<FileUploadFieldProps, 'setFiles' | 'files'> & {
        withSizeLimitMessage?: boolean;
    };

export function ControlledFileUpload<T extends FieldValues = FieldValues>(
    props: ControlledMultiSelectProps<T>,
) {
    const formContext = useFormContext<T>();
    const {
        name,
        control = formContext.control,
        defaultValue,
        rules,
        disabled,
        shouldUnregister,
        required,
        asterix,
        ...inputProps
    } = props;

    const validationRules = {
        ...rules,
        required: { value: !!required, message: 'This field is required' },
    };

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            rules={validationRules}
            disabled={disabled}
            shouldUnregister={shouldUnregister}
            render={({ field, formState }) => {
                const errorMessage = formState.errors[field.name]?.message as string;
                const isError = Boolean(errorMessage);
                const errorsFeedback: Feedback[] | undefined = isError
                    ? [{ message: errorMessage, state: 'error', hideIcon: true }]
                    : undefined;
                const feedback = [...(inputProps.feedback || []), ...(errorsFeedback || [])];
                return (
                    <SupportRequestsFileUpload
                        isError={feedback.some(({ state }) => state === 'error')}
                        disabled={disabled}
                        {...inputProps}
                        {...field}
                        setFiles={field.onChange}
                        feedback={feedback}
                        files={field.value}
                        asterix={asterix}
                    />
                );
            }}
        />
    );
}
